import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Printer, CalendarClock, CheckSquare, Bell, Target, Route } from "lucide-react";
import api from "../lib/api";
import { fmtDate, fmtCurrency } from "../lib/format";
import { printHTML } from "../lib/integrations";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";

const isSameDay = (d, ref) => { const x = new Date(d); return !isNaN(x) && x.toDateString() === ref.toDateString(); };
const isPast = (d, ref) => { const x = new Date(d); return !isNaN(x) && x < ref; };

export default function TodaysPlan() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [raw, setRaw] = useState({ meetings: [], tasks: [], opps: [], reminders: [] });
  const today = useMemo(() => new Date(), []);
  const startOfToday = useMemo(() => new Date(today.toDateString()), [today]);

  useEffect(() => {
    Promise.all([
      api.get("/meetings"), api.get("/tasks"), api.get("/opportunities"), api.get("/reminders?window=3"),
    ]).then(([m, t, o, r]) => {
      setRaw({ meetings: m.data, tasks: t.data, opps: o.data, reminders: r.data.items || [] });
    });
  }, []);

  const seesAll = ["admin", "director", "finance"].includes(user?.role);
  const mine = (name) => seesAll || !name || name === user?.name;

  const plan = useMemo(() => {
    const meetingsToday = raw.meetings.filter((m) => isSameDay(m.date, today) && mine(m.bdm_name));
    const tasksDue = raw.tasks.filter((t) => t.status !== "Done" && mine(t.assigned_to_name) &&
      (isSameDay(t.due_date, today) || isPast(t.due_date, startOfToday)));
    const followUps = raw.meetings.filter((m) => m.follow_up_date && mine(m.bdm_name) &&
      (isSameDay(m.follow_up_date, today) || isPast(m.follow_up_date, startOfToday)));
    const reminders = raw.reminders.filter((r) => r.category === "Birthday" || r.category === "Warranty" || r.category === "Service" || r.category === "Contract");
    const openOpps = raw.opps.filter((o) => !["Won", "Lost"].includes(o.stage) && mine(o.bdm_name))
      .sort((a, b) => Number(b.value || 0) - Number(a.value || 0)).slice(0, 6);
    return { meetingsToday, tasksDue, followUps, reminders, openOpps };
  }, [raw, today, startOfToday, user]); // eslint-disable-line

  const print = () => {
    const dateStr = today.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const row = (cells) => `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`;
    const section = (title, headers, rows) =>
      `<h2>${title}</h2>` + (rows.length
        ? `<table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${rows.join("")}</tbody></table>`
        : `<div class="empty">Nothing scheduled.</div>`);

    const overdueTag = (d) => isPast(d, startOfToday) && !isSameDay(d, today) ? ' <span class="overdue">(overdue)</span>' : "";

    const html = `
      <div class="head">
        <div class="brand">Dermalis CRM<small>Today's Plan</small></div>
        <div class="meta"><strong>${user?.name || ""}</strong><br>${user?.title || ""}<br>${dateStr}</div>
      </div>
      <div class="stats">
        <div class="stat"><b>${plan.meetingsToday.length}</b><span>Meetings</span></div>
        <div class="stat"><b>${plan.tasksDue.length}</b><span>Tasks due</span></div>
        <div class="stat"><b>${plan.followUps.length}</b><span>Follow-ups</span></div>
        <div class="stat"><b>${plan.openOpps.length}</b><span>Live deals</span></div>
      </div>
      ${section("Today's Meetings", ["Time", "Type", "Clinic", "Location"],
        plan.meetingsToday.map((m) => row([m.time || "—", m.meeting_type || "", m.clinic_name || "", m.location || ""])))}
      ${section("Tasks Due", ["Task", "Clinic", "Priority", "Due"],
        plan.tasksDue.map((t) => row([t.title || "", t.clinic_name || "", `<span class="pill">${t.priority || ""}</span>`, fmtDate(t.due_date) + overdueTag(t.due_date)])))}
      ${section("Follow-ups", ["Clinic", "From meeting", "Follow-up date"],
        plan.followUps.map((m) => row([m.clinic_name || "", m.meeting_type || "", fmtDate(m.follow_up_date) + overdueTag(m.follow_up_date)])))}
      ${section("Reminders (next 3 days)", ["Type", "Detail", "When"],
        plan.reminders.map((r) => row([`<span class="pill">${r.category}</span>`, `${r.title} — ${r.subtitle || ""}`, fmtDate(r.date)])))}
      ${section("Deals to Progress", ["Product", "Clinic", "Stage", "Value"],
        plan.openOpps.map((o) => row([o.product || "", o.clinic_name || "", `<span class="pill">${o.stage}</span>`, fmtCurrency(o.value)])))}
      <div class="foot">Generated by Dermalis CRM · ${new Date().toLocaleString("en-NZ")} · Confidential</div>
    `;
    printHTML(html, `Today's Plan - ${user?.name || "BDM"}`);
  };

  const Block = ({ title, icon: Icon, items, render, empty }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
      <h3 className="font-display font-semibold text-navy-900 flex items-center gap-2 mb-3"><Icon className="h-4 w-4 text-brand" /> {title} <span className="text-sm font-normal text-slate-400">({items.length})</span></h3>
      {items.length === 0 ? <p className="text-sm text-slate-400">{empty}</p> : <div className="space-y-2">{items.map(render)}</div>}
    </div>
  );

  return (
    <div>
      <PageHeader title="Today's Plan" subtitle={today.toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} icon={ClipboardList}>
        <Button onClick={print} className="bg-navy-900 hover:bg-navy-800" data-testid="print-plan-btn">
          <Printer className="h-4 w-4 mr-1.5" /> Print / Save as PDF
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[["Meetings", plan.meetingsToday.length, CalendarClock], ["Tasks Due", plan.tasksDue.length, CheckSquare], ["Follow-ups", plan.followUps.length, Bell], ["Live Deals", plan.openOpps.length, Target]].map(([l, v, Icon]) => (
          <div key={l} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid={`plan-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
            <Icon className="h-5 w-5 text-brand mb-2" />
            <div className="font-display text-2xl font-bold text-navy-900">{v}</div>
            <div className="text-xs text-slate-400">{l}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Block title="Today's Meetings" icon={CalendarClock} items={plan.meetingsToday} empty="No meetings scheduled today."
          render={(m) => (
            <div key={m.id} onClick={() => navigate("/meetings")} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer">
              <div><div className="font-medium text-navy-900 text-sm">{m.clinic_name}</div><div className="text-xs text-slate-400">{m.meeting_type} · {m.location}</div></div>
              <span className="text-sm font-semibold text-navy-900">{m.time || "—"}</span>
            </div>
          )} />
        <Block title="Tasks Due" icon={CheckSquare} items={plan.tasksDue} empty="No tasks due. Nice work!"
          render={(t) => (
            <div key={t.id} onClick={() => navigate("/tasks")} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer">
              <div><div className="font-medium text-navy-900 text-sm">{t.title}</div><div className="text-xs text-slate-400">{t.clinic_name}</div></div>
              <span className={`text-xs font-medium ${isPast(t.due_date, startOfToday) && !isSameDay(t.due_date, today) ? "text-red-600" : "text-slate-500"}`}>{fmtDate(t.due_date)}</span>
            </div>
          )} />
        <Block title="Follow-ups" icon={Bell} items={plan.followUps} empty="No follow-ups outstanding."
          render={(m) => (
            <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100">
              <div><div className="font-medium text-navy-900 text-sm">{m.clinic_name}</div><div className="text-xs text-slate-400">{m.meeting_type}</div></div>
              <span className="text-xs text-slate-500">{fmtDate(m.follow_up_date)}</span>
            </div>
          )} />
        <Block title="Deals to Progress" icon={Target} items={plan.openOpps} empty="No open opportunities."
          render={(o) => (
            <div key={o.id} onClick={() => navigate("/pipeline")} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer">
              <div><div className="font-medium text-navy-900 text-sm">{o.product}</div><div className="text-xs text-slate-400">{o.clinic_name} · {o.stage}</div></div>
              <span className="text-sm font-semibold text-navy-900">{fmtCurrency(o.value)}</span>
            </div>
          )} />
      </div>
    </div>
  );
}
